import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { SwUpdate, UnrecoverableStateEvent } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { interval, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class PwaService implements OnDestroy {
    private intervalSubscription: Subscription | null = null;
    private destroy$ = new Subject<void>();

    constructor(
        private swUpdate: SwUpdate,
        private snackBar: MatSnackBar,
        private zone: NgZone
    ) {
        this.initializeUpdateChecking();
    }

    private initializeUpdateChecking(): void {
        if (!this.swUpdate.isEnabled) {
            console.log('[PWA] Service Worker is not enabled');
            return;
        }

        console.log(
            '[PWA] Service Worker is enabled, initializing update checking'
        );

        // Check for updates on service worker initialization
        this.checkForUpdate();

        // Subscribe to all version update events for debugging
        this.swUpdate.versionUpdates
            .pipe(takeUntil(this.destroy$))
            .subscribe(event => {
                console.log('[PWA] Version update event:', event.type, event);

                if (event.type === 'VERSION_READY') {
                    this.activateUpdateSilently();
                } else if (event.type === 'VERSION_DETECTED') {
                    console.log('[PWA] New version detected, downloading...');
                } else if (event.type === 'VERSION_INSTALLATION_FAILED') {
                    console.error('[PWA] Version installation failed:', event);
                }
            });

        // Handle unrecoverable state - requires page reload
        this.swUpdate.unrecoverable
            .pipe(takeUntil(this.destroy$))
            .subscribe((event: UnrecoverableStateEvent) => {
                console.error('[PWA] Unrecoverable state:', event.reason);
                this.promptUnrecoverableState(event);
            });

        // Set up periodic update checks
        this.setupPeriodicUpdateChecks();
    }

    private checkForUpdate(): void {
        console.log('[PWA] Checking for updates...');
        this.swUpdate
            .checkForUpdate()
            .then(updateFound => {
                console.log(
                    '[PWA] Update check completed. Update found:',
                    updateFound
                );
            })
            .catch(err => {
                console.error('[PWA] Error checking for updates:', err);
            });
    }

    private setupPeriodicUpdateChecks(): void {
        this.zone.runOutsideAngular(() => {
            // Check every 5 minutes (reduced from 15 for faster detection)
            this.intervalSubscription = interval(5 * 60 * 1000)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.zone.run(() => this.checkForUpdate());
                });
        });
    }

    private activateUpdateSilently(): void {
        console.log('[PWA] Activating update silently...');
        this.swUpdate
            .activateUpdate()
            .then(() => {
                console.log(
                    '[PWA] Update activated. New version will be used on next refresh.'
                );
            })
            .catch(err => {
                console.error('[PWA] Error activating update:', err);
            });
    }

    private promptUnrecoverableState(event: UnrecoverableStateEvent): void {
        console.error(
            '[PWA] Service worker in unrecoverable state:',
            event.reason
        );

        const snackBarRef = this.snackBar.open(
            'Die App muss neu geladen werden.',
            'Neu laden',
            {
                duration: 0,
                panelClass: ['update-snackbar'],
            }
        );

        snackBarRef.onAction().subscribe(() => {
            window.location.reload();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.intervalSubscription) {
            this.intervalSubscription.unsubscribe();
        }
    }
}
